import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Truck, Calendar, Zap, Star, MapPin } from 'lucide-react';
// --- Live yards ticker -------------------------------------------------------
const DELIVERIES_ANCHOR = new Date('2025-05-01T00:00:00');
const DELIVERIES_BASE = 900;
const AVG_YARDS_PER_LOAD = 11;
function seededRand(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6;
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 8 * 60;
  return 11 * 60;
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 7 * 60) return 0;
    if (minutes >= 15 * 60) return 8 * 60;
    return minutes - 7 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) total += Math.floor(todayTotal * (elapsed / fullDay));
  }
  return total;
}
function useLiveYards() {
  const compute = () => Math.round(computeDeliveriesCount(new Date()) * AVG_YARDS_PER_LOAD / 10) * 10;
  const [yards, setYards] = useState(() => compute());
  useEffect(() => {
    const id = setInterval(() => setYards(compute()), 60000);
    return () => clearInterval(id);
  }, []);
  return yards;
}
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    if (display === value) return;
    const start = display;
    const diff = value - start;
    const duration = 900;
    const startTs = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + diff * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{display.toLocaleString()}</>;
}
type Stat = {
  key: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  value: React.ReactNode;
  suffix?: string;
  label: string;
  live?: boolean;
};
// Slim, scrollable stats bar — uses a consistent deep-green palette in both
// light and dark mode so it acts as a visual divider between page sections.
export function ByTheNumbers() {
  const yards = useLiveYards();
  const stats: Stat[] = [{
    key: 'yards',
    icon: Truck,
    value: <AnimatedNumber value={yards} data-id="element-179" />,
    suffix: '+',
    label: 'Yards Delivered',
    live: true
  }, {
    key: 'sameday',
    icon: Zap,
    value: 95,
    suffix: '%',
    label: 'Same-Day Delivery'
  }, {
    key: 'reviews',
    icon: Star,
    value: 250,
    suffix: '+',
    label: '5-Star Reviews'
  }, {
    key: 'zips',
    icon: MapPin,
    value: 87,
    label: 'ZIP Codes Served'
  }, {
    key: 'years',
    icon: Calendar,
    value: 4,
    label: 'Years in Business'
  }];
  return <section aria-label="By the numbers" className="w-full border-y border-[#1DB954]/30 bg-[#09281D]" data-id="element-180">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6" data-id="element-181">
        {/* Eyebrow */}
        <div className="flex items-center justify-center mb-4 md:mb-5" data-id="element-182">
          <div className="h-px w-8 bg-[#1DB954]/40" data-id="element-183" />
          <span className="px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6FE39A]" data-id="element-184">
            By The Numbers
          </span>
          <div className="h-px w-8 bg-[#1DB954]/40" data-id="element-185" />
        </div>

        {/* Stats row — horizontal scroll on mobile, even grid on md+ */}
        <ul className="flex md:grid md:grid-cols-5 gap-0 overflow-x-auto md:overflow-visible
                     -mx-4 sm:-mx-6 md:mx-0 px-4 sm:px-6 md:px-0
                     snap-x snap-mandatory md:snap-none scrollbar-hide" data-id="element-186">
          {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return <motion.li key={stat.key} initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: idx * 0.06,
            ease: 'easeOut'
          }} className={`shrink-0 md:shrink min-w-[170px] md:min-w-0 snap-start
                            flex items-center gap-3 px-4 md:px-5 py-2
                            ${idx > 0 ? 'md:border-l border-[#1DB954]/15' : ''}`} data-id="element-187">
                <div className="relative shrink-0" data-id="element-188">
                  <Icon className="w-5 h-5 text-[#6FE39A]" strokeWidth={1.5} data-id="element-189" />
                  {stat.live && <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2" data-id="element-190">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1DB954] opacity-75" data-id="element-191" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1DB954]" data-id="element-192" />
                    </span>}
                </div>

                <div className="min-w-0" data-id="element-193">
                  <p className="text-sm font-extrabold text-white leading-tight tabular-nums font-heading" data-id="element-194">
                    {stat.value}
                    {stat.suffix && <span className="text-[#6FE39A]" data-id="element-195">{stat.suffix}</span>}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/60 leading-tight mt-0.5" data-id="element-196">
                    {stat.label}
                  </p>
                </div>
              </motion.li>;
        })}
        </ul>
      </div>
    </section>;
}